import { getApiClient } from "@src/api";
import {
	invalidateNavbarBranchSnapshots,
	optimisticallyRemoveNavbarItem,
	restoreNavbarBranchSnapshots,
} from "@src/components/board/mutations/navbar-optimistic";
import { navbarBranchQueryKeys } from "@src/components/board/queries/fetch-ranklist";
import { trashQueryKeys } from "@src/components/board/queries/query-keys";
import { parseWhiteboardDocumentId } from "@src/components/collaboration/document-id";
import { graphql } from "@src/gql/gql";
import { handleError } from "@src/utils/error-handling";
import { showSuccessNotification } from "@src/utils/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const archiveWhiteboardQuery = graphql(/* GraphQL */ `
	mutation archiveWhiteboard($id: String!) {
		whiteboards {
			archive(whiteboardId: $id) {
				status
				name
				ownerId
			}
		}
	}
`);

/**
 * Soft-delete a whiteboard (moves it to trash).
 *
 * @returns React Query mutation object
 */
export const useArchiveWhiteboard = (showNotification = true) => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (whiteboardId: string) => {
			const result = await client.mutate({
				mutation: archiveWhiteboardQuery,
				variables: {
					id: parseWhiteboardDocumentId(whiteboardId).toIdString(),
				},
			});

			return result.data;
		},
		onMutate: async (whiteboardId) => {
			const snapshots = await optimisticallyRemoveNavbarItem(
				queryClient,
				whiteboardId,
			);
			return { snapshots };
		},
		onSuccess: () => {
			if (showNotification) {
				showSuccessNotification("Success", "Whiteboard moved to trash");
			}
		},
		onError: (error, _vars, context) => {
			if (context?.snapshots) {
				restoreNavbarBranchSnapshots(queryClient, context.snapshots);
			}
			handleError(error, "Failed to delete whiteboard");
		},
		onSettled: (_data, _error, _vars, context) => {
			invalidateNavbarBranchSnapshots(queryClient, context?.snapshots ?? []);
			void queryClient.invalidateQueries({
				queryKey: trashQueryKeys.all(),
			});
			void queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
		},
	});
};
